import { useState, useEffect, useRef, useMemo, useCallback, memo } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '@/contexts/ThemeContext';

export interface TimePickerProps {
  value?: string; // Format: "HH:mm"
  placeholder?: string;
  disabled?: boolean;
  minuteStep?: number;
  onChange?: (value: string) => void;
  onChangeEvent?: (value: string) => void;
}

function pad(num: number): string {
  return num < 10 ? '0' + num : num.toString();
}

export const TimePicker = memo(function TimePicker({
  value = '',
  placeholder = 'Time',
  disabled = false,
  minuteStep = 1,
  onChange,
  onChangeEvent,
}: TimePickerProps) {
  const { theme } = useTheme();
  const [open, setOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState<number | null>(null);
  const [selectedMinute, setSelectedMinute] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  const rootRef = useRef<HTMLDivElement>(null);
  const justOpenedRef = useRef(false);

  // Parse value prop into hour/minute
  const parseValue = useCallback((val: string) => {
    if (!val) {
      setSelectedHour(null);
      setSelectedMinute(null);
      return;
    }
    const parts = val.split(':');
    if (parts.length >= 2) {
      const h = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10);
      if (!isNaN(h) && h >= 0 && h < 24) {
        setSelectedHour(h);
      }
      if (!isNaN(m) && m >= 0 && m < 60) {
        setSelectedMinute(m);
      }
    }
  }, []);

  // Watch value prop
  useEffect(() => {
    parseValue(value);
  }, [value, parseValue]);

  // Computed: display value
  const displayValue = useMemo(() => {
    if (selectedHour !== null && selectedMinute !== null) {
      return pad(selectedHour) + ':' + pad(selectedMinute);
    }
    return '';
  }, [selectedHour, selectedMinute]);

  // Computed: hours array
  const hours = useMemo(() => {
    const arr: number[] = [];
    for (let i = 0; i < 24; i++) {
      arr.push(i);
    }
    return arr;
  }, []);

  // Computed: minutes array
  const minutes = useMemo(() => {
    const arr: number[] = [];
    for (let i = 0; i < 60; i += minuteStep) {
      arr.push(i);
    }
    return arr;
  }, [minuteStep]);

  // Check mobile
  const checkMobile = useCallback(() => {
    setIsMobile(prev => {
      const nowMobile = window.innerWidth <= 768;
      if (prev !== nowMobile) {
        // Close dropdown if switching between mobile/desktop while open
        setOpen(false);
      }
      return nowMobile;
    });
  }, []);

  // Update dropdown position (rAF-throttled)
  const dropdownRafRef = useRef(0);
  const updateDropdownPosition = useCallback(() => {
    if (isMobile) return;

    if (dropdownRafRef.current) cancelAnimationFrame(dropdownRafRef.current);
    dropdownRafRef.current = requestAnimationFrame(() => {
      dropdownRafRef.current = 0;
      const el = rootRef.current;
      if (!el) return;

      const control = el.querySelector('.kst-time-picker__control') as HTMLElement;
      const dropdown = el.querySelector('.kst-time-picker__dropdown') as HTMLElement;
      if (!control) return;

      const rect = control.getBoundingClientRect();
      const dropdownWidth = 200;
      let dropdownHeight = dropdown ? dropdown.offsetHeight : 270;
      if (dropdownHeight < 100) dropdownHeight = 270;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const paddingVal = 8;

      const spaceBelow = viewportHeight - rect.bottom - paddingVal;
      const spaceAbove = rect.top - paddingVal;
      const showAbove = spaceBelow < dropdownHeight && spaceAbove >= dropdownHeight;

      let top: string;
      if (showAbove) {
        top = (rect.top - dropdownHeight - 4) + 'px';
      } else {
        top = (rect.bottom + 4) + 'px';
      }

      let left = rect.left;
      const spaceOnRight = viewportWidth - rect.left;

      if (spaceOnRight < dropdownWidth + paddingVal) {
        left = rect.right - dropdownWidth;
      }
      if (left + dropdownWidth > viewportWidth - paddingVal) {
        left = viewportWidth - dropdownWidth - paddingVal;
      }
      if (left < paddingVal) {
        left = paddingVal;
      }

      const finalWidth = Math.min(dropdownWidth, viewportWidth - paddingVal * 2);

      setDropdownStyle({
        position: 'fixed',
        top: top,
        left: left + 'px',
        width: finalWidth + 'px',
        zIndex: 9999,
      });
    });
  }, [isMobile]);

  // Scroll helpers
  const scrollToSelected = useCallback(() => {
    const el = rootRef.current;
    if (!el) return;

    let dropdown = el.querySelector('.kst-time-picker__dropdown') as HTMLElement;
    if (!dropdown) {
      dropdown = document.querySelector('.kst-time-picker__modal') as HTMLElement;
    }
    if (!dropdown) return;

    // Scroll hour column
    if (selectedHour !== null) {
      const hourItems = dropdown.querySelectorAll('.time-column-hour .column-item');
      if (hourItems[selectedHour]) {
        hourItems[selectedHour].scrollIntoView({ block: 'center' });
      }
    }

    // Scroll minute column
    if (selectedMinute !== null) {
      const minuteIndex = Math.floor(selectedMinute / minuteStep);
      const minuteItems = dropdown.querySelectorAll('.time-column-minute .column-item');
      if (minuteIndex >= 0 && minuteItems[minuteIndex]) {
        minuteItems[minuteIndex].scrollIntoView({ block: 'center' });
      }
    }
  }, [selectedHour, selectedMinute, minuteStep]);

  const scrollSelectedToCenter = useCallback((columnType: string) => {
    const el = rootRef.current;
    if (!el) return;

    const dropdown = el.querySelector('.kst-time-picker__dropdown') as HTMLElement;
    if (!dropdown) return;

    if (columnType === 'hour' && selectedHour !== null) {
      const hourItems = dropdown.querySelectorAll('.time-column-hour .column-item');
      if (hourItems[selectedHour]) {
        hourItems[selectedHour].scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    } else if (columnType === 'minute' && selectedMinute !== null) {
      const minuteIndex = Math.floor(selectedMinute / minuteStep);
      const minuteItems = dropdown.querySelectorAll('.time-column-minute .column-item');
      if (minuteIndex >= 0 && minuteItems[minuteIndex]) {
        minuteItems[minuteIndex].scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    }
  }, [selectedHour, selectedMinute, minuteStep]);

  // Watch open state
  useEffect(() => {
    if (open) {
      justOpenedRef.current = true;
      updateDropdownPosition();

      // After render, scroll to selected and re-update position
      const rafId = requestAnimationFrame(() => {
        scrollToSelected();
        setTimeout(() => {
          updateDropdownPosition();
        }, 100);
      });

      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);

      const justOpenedTimer = setTimeout(() => {
        justOpenedRef.current = false;
      }, 200);

      return () => {
        cancelAnimationFrame(rafId);
        clearTimeout(justOpenedTimer);
        window.removeEventListener('scroll', updateDropdownPosition, true);
        window.removeEventListener('resize', updateDropdownPosition);
      };
    }
  }, [open, updateDropdownPosition, scrollToSelected]);

  // Mounted / cleanup: mobile check, click outside
  useEffect(() => {
    setIsMobile(window.innerWidth <= 768); // immediate initial check
    let mobileTimer: ReturnType<typeof setTimeout> | null = null;
    const debouncedCheckMobile = () => {
      if (mobileTimer) clearTimeout(mobileTimer);
      mobileTimer = setTimeout(() => checkMobile(), 150);
    };
    window.addEventListener('resize', debouncedCheckMobile);

    const onClickOutside = (e: MouseEvent | TouchEvent) => {
      // Use a ref-based check since this runs as a listener
      const target = e.target as HTMLElement;
      const el = rootRef.current;
      const inComponent = el && el.contains(target);

      if (justOpenedRef.current) return;

      if (window.innerWidth <= 768) {
        const modal = document.querySelector('.kst-time-picker__modal-backdrop');
        const inModal = modal && modal.contains(target);
        if (!inComponent && !inModal) {
          setOpen(false);
        }
      } else {
        if (!inComponent) {
          setOpen(false);
        }
      }
    };

    const timer = setTimeout(() => {
      document.addEventListener('mouseup', onClickOutside, true);
      document.addEventListener('touchend', onClickOutside, { passive: true, capture: true } as any);
    }, 100);

    return () => {
      clearTimeout(timer);
      if (mobileTimer) clearTimeout(mobileTimer);
      document.removeEventListener('mouseup', onClickOutside, true);
      document.removeEventListener('touchend', onClickOutside, true);
      window.removeEventListener('resize', debouncedCheckMobile);
    };
  }, [checkMobile]);

  // Emit value helper
  const emitValue = useCallback((hour: number | null, minute: number | null) => {
    if (hour !== null && minute !== null) {
      const val = pad(hour) + ':' + pad(minute);
      onChange?.(val);
      onChangeEvent?.(val);
    }
  }, [onChange, onChangeEvent]);

  // Toggle open
  const toggleOpen = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    setOpen(prev => !prev);
  }, [disabled]);

  // Close
  const close = useCallback(() => {
    setOpen(false);
  }, []);

  // Select hour
  const selectHour = useCallback((hour: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedHour(hour);
    // We need to emit with the new hour and current minute
    setSelectedMinute(prev => {
      emitValue(hour, prev);
      return prev;
    });
    // Scroll after state update
    requestAnimationFrame(() => {
      scrollSelectedToCenter('hour');
    });
  }, [emitValue, scrollSelectedToCenter]);

  // Select minute
  const selectMinute = useCallback((minute: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedMinute(minute);
    // We need to emit with current hour and new minute
    setSelectedHour(prev => {
      emitValue(prev, minute);
      return prev;
    });
    // Auto-close after selecting minute
    close();
  }, [emitValue, close]);

  // Backdrop click
  const onBackdropClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.classList.contains('kst-time-picker__modal-backdrop')) {
      close();
    }
  }, [close]);

  // Render time columns (shared between desktop and mobile)
  const renderTimeColumns = () => (
    <div className="time-columns">
      <div className="time-column time-column-hour">
        <div className="column-header">Hour</div>
        <div className="column-list">
          {hours.map(hour => (
            <div
              key={'h' + hour}
              className={'column-item' + (hour === selectedHour ? ' column-item--selected' : '')}
              onClick={(e) => selectHour(hour, e)}
            >
              {pad(hour)}
            </div>
          ))}
        </div>
      </div>
      <div className="time-column time-column-minute">
        <div className="column-header">Min</div>
        <div className="column-list">
          {minutes.map(minute => (
            <div
              key={'m' + minute}
              className={'column-item' + (minute === selectedMinute ? ' column-item--selected' : '')}
              onClick={(e) => selectMinute(minute, e)}
            >
              {pad(minute)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="kst-time-picker" ref={rootRef}>
      <div
        className={
          'kst-time-picker__control' +
          (open ? ' kst-time-picker__control--open' : '') +
          (disabled ? ' kst-time-picker__control--disabled' : '')
        }
        onClick={toggleOpen}
      >
        <div className={'kst-time-picker__value' + (!displayValue ? ' kst-time-picker__value--placeholder' : '')}>
          {displayValue || placeholder}
        </div>
        <div className="kst-time-picker__icon">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
            <path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>

      {/* Desktop dropdown */}
      {!isMobile && open && (
        <div className="kst-time-picker__dropdown" style={dropdownStyle}>
          {renderTimeColumns()}
        </div>
      )}

      {/* Mobile modal — portaled to document.body so it escapes any ancestor
          that clips/constrains fixed positioning (e.g. a modal dialog body with
          overflow:auto / -webkit-overflow-scrolling on iOS). The theme class is
          re-applied on the portal wrapper since the themed root is left behind. */}
      {isMobile && open && createPortal(
        <div className={'kst-time-picker' + (theme ? ' ' + theme : '')}>
          <div className="kst-time-picker__modal-backdrop" onClick={onBackdropClick}>
            <div className="kst-time-picker__modal" onClick={(e) => e.stopPropagation()}>
              <div className="kst-time-picker__modal-header">
                <div className="kst-time-picker__modal-title">Select Time</div>
                <button type="button" className="kst-time-picker__modal-close" onClick={close}>
                  &times;
                </button>
              </div>
              {renderTimeColumns()}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
});

(window as any).TimePickerReact = TimePicker;
